<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { formatPrice, type Product } from "../data/products";
import { useCart } from "../composables/useCart";
import ProductArt from "./ProductArt.vue";

const props = defineProps<{ product: Product | null }>();
const emit = defineEmits<{ (e: "close"): void }>();

const cart = useCart();
const selectedSize = ref<string>("");
const selectedSwatch = ref<string>("");

watch(
  () => props.product,
  (p) => {
    if (p) {
      selectedSize.value = p.sizes[Math.min(2, p.sizes.length - 1)];
      selectedSwatch.value = p.swatches[0].name;
    }
  },
  { immediate: true }
);

const isOpen = computed(() => props.product != null);

const handleAdd = () => {
  if (!props.product) return;
  cart.add(props.product, selectedSize.value, selectedSwatch.value);
  emit("close");
};

const onBackdrop = (event: MouseEvent) => {
  if ((event.target as HTMLElement).classList.contains("modal__backdrop")) {
    emit("close");
  }
};
</script>

<template>
  <Transition name="modal">
    <div v-if="isOpen && product" class="modal__backdrop" @click="onBackdrop">
      <div class="modal" role="dialog" aria-modal="true">
        <button class="modal__close" @click="emit('close')" aria-label="Close">
          <svg viewBox="0 0 20 20" width="14" height="14" fill="none">
            <path d="M4 4l12 12M16 4L4 16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
          </svg>
        </button>
        <div class="modal__art">
          <img
            v-if="product.image"
            :src="product.image"
            :alt="product.name"
            class="modal__image"
            decoding="async"
          />
          <ProductArt v-else :product="product" aspect="4 / 5" />
        </div>
        <div class="modal__body">
          <span class="os1-eyebrow">{{ product.category.toUpperCase() }}</span>
          <h2 class="modal__name">{{ product.name }}</h2>
          <div class="modal__price-line">
            <span class="modal__price">{{ formatPrice(product.price) }}</span>
            <span class="os1-tag" v-if="product.badge">{{ product.badge }}</span>
          </div>
          <p class="modal__story">{{ product.story }}</p>

          <div class="modal__group">
            <span class="modal__label">Color · {{ selectedSwatch }}</span>
            <div class="modal__swatches">
              <button
                v-for="s in product.swatches"
                :key="s.name"
                :class="['modal__swatch', { 'is-active': selectedSwatch === s.name }]"
                :style="{ background: s.hex }"
                :title="s.name"
                @click="selectedSwatch = s.name"
              />
            </div>
          </div>

          <div class="modal__group">
            <span class="modal__label">Size</span>
            <div class="modal__sizes">
              <button
                v-for="size in product.sizes"
                :key="size"
                :class="['modal__size', { 'is-active': selectedSize === size }]"
                @click="selectedSize = size"
              >
                {{ size }}
              </button>
            </div>
          </div>

          <div class="modal__actions">
            <button class="os1-button os1-button--solid" @click="handleAdd">
              Add to bag — {{ formatPrice(product.price) }}
            </button>
            <button class="os1-button os1-button--ghost" @click="emit('close')">
              Continue browsing
            </button>
          </div>

          <ul class="modal__details">
            <li v-for="(d, i) in product.details" :key="i">{{ d }}</li>
          </ul>

          <div class="modal__meta">
            <div>
              <span class="modal__meta-key">Fabric</span>
              <span>{{ product.fabric }}</span>
            </div>
            <div>
              <span class="modal__meta-key">Origin</span>
              <span>{{ product.origin }}</span>
            </div>
            <div>
              <span class="modal__meta-key">Code</span>
              <span class="modal__meta-mono">{{ product.id.toUpperCase() }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.modal__backdrop {
  position: fixed;
  inset: 0;
  z-index: 80;
  background: rgba(6, 1, 1, 0.78);
  backdrop-filter: blur(10px);
  display: grid;
  place-items: center;
  padding: clamp(0.5rem, 3vw, 2rem);
}

.modal {
  position: relative;
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 0;
  width: min(100%, 1080px);
  max-height: 90vh;
  background: linear-gradient(180deg, #1c0808 0%, #160606 100%);
  border: 1px solid var(--os1-line-strong);
  border-radius: var(--os1-radius);
  overflow: hidden;
  box-shadow: var(--os1-shadow);
}

.modal__close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 5;
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border-radius: 999px;
  background: rgba(8, 2, 2, 0.7);
  border: 1px solid var(--os1-line-strong);
  color: var(--os1-ink);
}

.modal__close:hover {
  color: var(--os1-rose);
}

.modal__art {
  position: relative;
  overflow: hidden;
}

.modal__image {
  display: block;
  width: 100%;
  height: 100%;
  aspect-ratio: 4 / 5;
  object-fit: cover;
  background: #1a0606;
}

.modal__body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: clamp(1.4rem, 3vw, 2.4rem);
  overflow-y: auto;
}

.modal__name {
  font-size: var(--os1-step-3);
  color: var(--os1-paper);
  letter-spacing: -0.01em;
  line-height: 1;
}

.modal__price-line {
  display: flex;
  align-items: center;
  gap: 0.8rem;
}

.modal__price {
  font-family: var(--os1-font-mono);
  color: var(--os1-rose);
  font-size: 1.05rem;
}

.modal__story {
  color: var(--os1-ink-soft);
  font-size: 0.92rem;
  max-width: 46ch;
}

.modal__group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.4rem;
}

.modal__label {
  font-size: 0.65rem;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--os1-ink-mute);
}

.modal__swatches {
  display: flex;
  gap: 0.5rem;
}

.modal__swatch {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid rgba(241, 217, 200, 0.18);
  cursor: pointer;
  position: relative;
}

.modal__swatch.is-active {
  outline: 2px solid var(--os1-rose);
  outline-offset: 2px;
}

.modal__sizes {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.modal__size {
  min-width: 52px;
  padding: 0.55rem 0.7rem;
  border: 1px solid var(--os1-line);
  color: var(--os1-ink-soft);
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  border-radius: var(--os1-radius);
  transition: border-color 160ms ease, color 160ms ease, background 160ms ease;
}

.modal__size:hover {
  border-color: var(--os1-rose);
  color: var(--os1-rose);
}

.modal__size.is-active {
  background: var(--os1-paper);
  color: #1a0303;
  border-color: var(--os1-paper);
}

.modal__actions {
  display: flex;
  gap: 0.6rem;
  margin-top: 0.6rem;
  flex-wrap: wrap;
}

.modal__details {
  margin-top: 0.8rem;
  padding-top: 1rem;
  border-top: 1px solid var(--os1-line);
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  color: var(--os1-ink-soft);
  font-size: 0.82rem;
}

.modal__details li {
  display: flex;
  gap: 0.5rem;
}

.modal__details li::before {
  content: "·";
  color: var(--os1-red);
}

.modal__meta {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.6rem;
  margin-top: 0.6rem;
  padding-top: 0.9rem;
  border-top: 1px solid var(--os1-line);
}

.modal__meta div {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.modal__meta-key {
  font-size: 0.6rem;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--os1-ink-mute);
}

.modal__meta-mono {
  font-family: var(--os1-font-mono);
  color: var(--os1-ink);
}

@media (max-width: 820px) {
  .modal {
    grid-template-columns: 1fr;
    max-height: 96vh;
  }
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 220ms ease;
}
.modal-enter-active .modal,
.modal-leave-active .modal {
  transition: transform 240ms ease, opacity 220ms ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-from .modal,
.modal-leave-to .modal {
  transform: translateY(20px);
  opacity: 0;
}
</style>
